/**
 * Level (storey) z-bounds resolution — the ONE place that answers "what is
 * this floor's z-range, optionally padded".
 *
 * Semantics: `ceilingZ` is the TRUE ceiling (top of the storey's clear
 * interior). The structural slab occupies [ceilingZ, nextFloorZ]. Legacy
 * projects persisted the ceiling AT the next floor's height; the resolver
 * detects that (gap within {@link LEGACY_CONTIGUOUS_EPS_M}) and degrades
 * gracefully: z-ranges stay numerically identical to the old behavior and
 * `slabThickness` reads as null.
 *
 * Padding is ALWAYS applied locally by the consumer via {@link LevelPadding} —
 * never baked into persisted plane heights. (Floor detection once baked a
 * ±10 cm margin into the persisted floor plane; everything derived from it —
 * wall sketch elevation, opening sills, wall heights, elevation views — sat
 * 10 cm off the real slab. Keep slack in the query, not the data.)
 */

/** Minimal per-level plane snapshot (subset of FloorGroupData / FloorHeightInfo). */
export interface LevelPlanes {
  groupIdentifier: string;
  /** Floor plane world-Z. */
  floorZ: number;
  /** Stored ceiling plane world-Z, or null when never set / not meaningful. */
  ceilingZ: number | null;
}

/**
 * Padding request for a level z-range.
 * - `'none'`        exact [floorZ, ceilingZ].
 * - `'slabSurface'` ± {@link SLAB_SURFACE_MARGIN_M}: planes are fitted THROUGH
 *                   the slab's point scatter, so an exact clip cuts roughly
 *                   half the surface points (isolation clips, X-ray).
 * - `'toNextFloor'` top extended to the next level's floorZ so the structural
 *                   slab is included (content hanging under the slab: beams,
 *                   ducts). Top level: ceilingZ + {@link DEFAULT_SLAB_THICKNESS_M}.
 * - `{below,above}` explicit metres (capture boxes etc.).
 */
export type LevelPadding = 'none' | 'slabSurface' | 'toNextFloor' | { below?: number; above?: number };

/** Where the resolved ceilingZ came from. */
export type CeilingSource = 'stored' | 'nextFloor' | 'fallback';

export interface ResolvedLevelBounds {
  groupIdentifier: string;
  floorZ: number;
  /** True ceiling; fallback chain: stored → next floor's floorZ → floorZ + {@link FALLBACK_INTERIOR_HEIGHT_M}. */
  ceilingZ: number;
  ceilingSource: CeilingSource;
  /** Next level's floorZ strictly above this floorZ, or null (top level). */
  nextFloorZ: number | null;
  /**
   * nextFloorZ − ceilingZ when a stored ceiling and a next floor exist and the
   * gap exceeds {@link LEGACY_CONTIGUOUS_EPS_M}; null otherwise
   * (legacy-contiguous data, top level, or fallback ceiling).
   */
  slabThickness: number | null;
  /** Clear interior height, ceilingZ − floorZ. */
  interiorHeight: number;
}

export interface LevelZRange {
  minZ: number;
  maxZ: number;
}

/**
 * Vertical slack around the floor/ceiling planes for surface-preserving clips.
 * The planes are fitted THROUGH the slab's point scatter, so a zero-margin
 * clip cuts roughly half the surface points and makes the floor look sparse.
 */
export const SLAB_SURFACE_MARGIN_M = 0.1;

/**
 * Interior height assumed when a level has no usable ceiling and no floor
 * above. Single source for the skeleton FALLBACK_WALL_HEIGHT and the IFC
 * export default wall height (both 2.5 m historically).
 */
export const FALLBACK_INTERIOR_HEIGHT_M = 2.5;

/** Assumed structural slab thickness when the real one can't be derived (top level). */
export const DEFAULT_SLAB_THICKNESS_M = 0.3;

/**
 * Minimum height a stored ceiling must sit above the floor to count as real.
 * The `ceiling_plane_position` property defaults to {0,0,0}, so an unset
 * ceiling reads as z=0; requiring ceiling > floor + this clearance is robust
 * to the stored-vs-default ambiguity.
 */
export const MIN_CEILING_CLEARANCE_M = 0.05;

/**
 * A stored ceiling within this distance below the next floor is treated as
 * legacy-contiguous data (ceiling was persisted AT the next floor's height),
 * i.e. no real slab information exists.
 */
export const LEGACY_CONTIGUOUS_EPS_M = 0.02;

/** Floors closer than this in Z are considered the same level when searching for the next floor above. */
const NEXT_FLOOR_EPS_M = 1e-4;

/**
 * Whether a stored ceiling z is meaningful (see {@link MIN_CEILING_CLEARANCE_M}).
 * Single home for the guard previously duplicated across useFloorGroupsData,
 * FloorplanPersona and fetchFloorPlanes.
 */
export function isMeaningfulCeiling(floorZ: number, ceilingZ: number | null | undefined): ceilingZ is number {
  return ceilingZ != null && ceilingZ > floorZ + MIN_CEILING_CLEARANCE_M;
}

/**
 * Resolve one level's bounds against the full level list (the list is needed
 * to derive `nextFloorZ`). Returns null when `groupIdentifier` is not in
 * `levels`.
 */
export function resolveLevelBounds(levels: readonly LevelPlanes[], groupIdentifier: string): ResolvedLevelBounds | null {
  const level = levels.find((l) => l.groupIdentifier === groupIdentifier);
  if (!level) return null;

  let nextFloorZ: number | null = null;
  for (const other of levels) {
    if (other.floorZ > level.floorZ + NEXT_FLOOR_EPS_M && (nextFloorZ === null || other.floorZ < nextFloorZ)) {
      nextFloorZ = other.floorZ;
    }
  }

  let ceilingZ: number;
  let ceilingSource: CeilingSource;
  if (isMeaningfulCeiling(level.floorZ, level.ceilingZ)) {
    ceilingZ = level.ceilingZ;
    ceilingSource = 'stored';
  } else if (nextFloorZ !== null) {
    ceilingZ = nextFloorZ;
    ceilingSource = 'nextFloor';
  } else {
    ceilingZ = level.floorZ + FALLBACK_INTERIOR_HEIGHT_M;
    ceilingSource = 'fallback';
  }

  const slabThickness = ceilingSource === 'stored' && nextFloorZ !== null && nextFloorZ - ceilingZ > LEGACY_CONTIGUOUS_EPS_M ? nextFloorZ - ceilingZ : null;

  return {
    groupIdentifier,
    floorZ: level.floorZ,
    ceilingZ,
    ceilingSource,
    nextFloorZ,
    slabThickness,
    interiorHeight: ceilingZ - level.floorZ,
  };
}

/** Apply a padding request to resolved bounds. */
export function levelZRange(bounds: ResolvedLevelBounds, padding: LevelPadding = 'none'): LevelZRange {
  if (padding === 'none') {
    return { minZ: bounds.floorZ, maxZ: bounds.ceilingZ };
  }
  if (padding === 'slabSurface') {
    return { minZ: bounds.floorZ - SLAB_SURFACE_MARGIN_M, maxZ: bounds.ceilingZ + SLAB_SURFACE_MARGIN_M };
  }
  if (padding === 'toNextFloor') {
    return { minZ: bounds.floorZ, maxZ: bounds.nextFloorZ ?? bounds.ceilingZ + DEFAULT_SLAB_THICKNESS_M };
  }
  return { minZ: bounds.floorZ - (padding.below ?? 0), maxZ: bounds.ceilingZ + (padding.above ?? 0) };
}

/** One-shot convenience: resolve + pad. */
export function resolveLevelZRange(levels: readonly LevelPlanes[], groupIdentifier: string, padding: LevelPadding = 'none'): LevelZRange | null {
  const bounds = resolveLevelBounds(levels, groupIdentifier);
  return bounds ? levelZRange(bounds, padding) : null;
}

/** Default symmetric tolerance for {@link isZWithinLevel}. */
export const LEVEL_MEMBERSHIP_TOLERANCE_M = 0.5;

/**
 * Half-open level membership: z ∈ [floorZ − tolerance, nextFloorZ) — or
 * [floorZ − tolerance, ceilingZ + tolerance] for the top level. Standardizes
 * "does this annotation belong to this storey"; the half-open upper edge
 * prevents a room near the slab from being claimed by two stacked levels.
 */
export function isZWithinLevel(bounds: ResolvedLevelBounds, z: number, tolerance: number = LEVEL_MEMBERSHIP_TOLERANCE_M): boolean {
  if (z < bounds.floorZ - tolerance) return false;
  if (bounds.nextFloorZ !== null) return z < bounds.nextFloorZ;
  return z <= bounds.ceilingZ + tolerance;
}
